import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, BaseModelObject, ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class ProductBrand extends BaseModelObject<ProductBrand> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Product Brand Name
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public name: string = "";
    private _imageId: Guid | null = null;
    /**
    * Alternative Logo for Label Printing
    */
    public get imageId(): Guid | null { return this._imageId }
    public set imageId(value: Guid | null) {
      this._imageId = value === undefined ? null : value;
    }
    /**
    * Are sales currently allowed for products in this brand? (Use to prevent selling of Eg out-of-season products).
    */
    public allowSales: boolean = false;
    /**
    * Product Brand Notes
    * Not Nullable
    * Max Length : 100000
    */
    public notes: string = "";
    /**
    * Tasting Notes
    * Not Nullable
    * Max Length : 100000
    */
    public tastingNotes: string = "";
    /**
    * Ingredients and Allergens
    * Not Nullable
    * Max Length : 100000
    */
    public ingredientsAndAllergens: string = "";
    public isHidden: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.name = jsonSerializedObject.name;
          this.imageId = jsonSerializedObject.imageId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.imageId);
          this.allowSales = jsonSerializedObject.allowSales;
          this.notes = jsonSerializedObject.notes;
          this.tastingNotes = jsonSerializedObject.tastingNotes;
          this.ingredientsAndAllergens = jsonSerializedObject.ingredientsAndAllergens;
          this.isHidden = jsonSerializedObject.isHidden;
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.name = this.name;
        toRet.imageId = this.imageId == null ? null : this.imageId.toString();
        toRet.allowSales = this.allowSales;
        toRet.notes = this.notes;
        toRet.tastingNotes = this.tastingNotes;
        toRet.ingredientsAndAllergens = this.ingredientsAndAllergens;
        toRet.isHidden = this.isHidden;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "ProductBrand.Id cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
            {field: "imageId", rule: (v: any) => v == null || !v.isEmpty() || "ProductBrand.ImageId cannot be empty"},
            {field: "notes", rule: (v: any) => v == null || v.length <= 100000 || "Notes has too many characters (100000)"},
            {field: "tastingNotes", rule: (v: any) => v == null || v.length <= 100000 || "TastingNotes has too many characters (100000)"},
            {field: "ingredientsAndAllergens", rule: (v: any) => v == null || v.length <= 100000 || "IngredientsAndAllergens has too many characters (100000)"},
    ];
}


export class ProductBrandService extends ServiceBase {

  public async getAllProductBrands(tenantId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<ProductBrand[]> {
    const req = {
    tenantId:tenantId.toString(),
    };
    const extractor = (response:any) => response.productBrands.map((v: any) => new ProductBrand(v));;
    return this.makeJsonRequest("productBrand/v1/getAllProductBrands", req, false, extractor, opts);
  };

  public async getProductBrand(tenantId: Guid, productBrandId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<ProductBrand> {
    const req = {
    tenantId:tenantId.toString(),
    productBrandId:productBrandId.toString(),
    };
    const extractor = (response:any) => new ProductBrand(response.productBrand);;
    return this.makeJsonRequest("productBrand/v1/getProductBrand", req, false, extractor, opts);
  };

  public async getProductBrands(tenantId: Guid, productBrandIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<ProductBrand[]> {
    const req = {
    tenantId:tenantId.toString(),
    productBrandIds:productBrandIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.productBrands.map((v: any) => new ProductBrand(v));;
    return this.makeJsonRequest("productBrand/v1/getProductBrands", req, false, extractor, opts);
  };

  public async saveProductBrand(tenantId: Guid, productBrand: ProductBrand, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    productBrand:productBrand.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("productBrand/v1/saveProductBrand", req, true, extractor, opts);
  };

  public async setProductBrandIsHidden(tenantId: Guid, productBrandId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    productBrandId:productBrandId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("productBrand/v1/setProductBrandIsHidden", req, true, extractor, opts);
  };
}

export const productBrandService = new ProductBrandService();